import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import "@/App.css";
import { Instagram, Music, Mail, X, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_sound-collective-12/artifacts/4tppwx76_bravo%20niska%20logo%202.png";
const BACKGROUND_URL = "https://customer-assets-jt897jd0.emergentagent.net/job_sound-collective-12/artifacts/rbnveo1y_ChatGPT%20Image%20Jul%207%2C%202026%2C%2010_43_06%20PM-3000x3000.webp";
const BACKGROUND_VIDEO = "https://customer-assets-jt897jd0.emergentagent.net/job_sound-collective-12/artifacts/4mi0kwu2_bravo%20niska%208k%20website%20loop.mp4";

// Spotify Icon Component
const SpotifyIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

// TikTok Icon Component
const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Social Links Data
const socialLinks = [
  {
    label: "INSTAGRAM",
    url: "https://www.instagram.com/bravo_niska",
    icon: Instagram,
  },
  {
    label: "SOUNDCLOUD",
    url: "https://www.soundcloud.com/bravoniska",
    icon: Music,
  },
  {
    label: "SPOTIFY",
    url: "https://open.spotify.com/artist/0LABXGsIJPoRvryq6ET9ME?si=ysv6nQBsTOi-Phcsu7Yj8g",
    icon: SpotifyIcon,
    isCustomIcon: true,
  },
  {
    label: "TIKTOK",
    url: "https://www.tiktok.com/@bravoniska1",
    icon: TikTokIcon,
    isCustomIcon: true,
  },
];

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 1,
    },
  },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 },
};

function App() {
  const [showContactModal, setShowContactModal] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitStatus("success");
      setFormData({ name: "", email: "", message: "" });
      setTimeout(() => {
        setShowContactModal(false);
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="landing-container" data-testid="landing-page">
      {/* Background Video */}
      <video
        className="background-video"
        autoPlay
        loop
        muted
        playsInline
        poster={BACKGROUND_URL}
        aria-hidden="true"
      >
        <source src={BACKGROUND_VIDEO} type="video/mp4" />
      </video>
      <div className="background-overlay" aria-hidden="true" />

      {/* Content */}
      <div className="content-wrapper">
        {/* Logo */}
        <motion.div
          className="logo-container"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          <img
            src={LOGO_URL}
            alt="BRAVO NISKA"
            className="logo-image"
            data-testid="logo-image"
          />
        </motion.div>

        {/* Social Links */}
        <motion.div
          className="links-container"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {socialLinks.map((link) => (
            <motion.a
              key={link.label}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              variants={fadeInUp}
              transition={{ duration: 0.5, ease: "easeOut" }}
              data-testid={`social-link-${link.label.toLowerCase()}`}
            >
              {link.isCustomIcon ? (
                <link.icon size={18} />
              ) : (
                <link.icon size={18} strokeWidth={1.5} />
              )}
              <span>{link.label}</span>
            </motion.a>
          ))}

          {/* Contact/Booking Button */}
          <motion.button
            onClick={() => setShowContactModal(true)}
            className="social-link contact-btn"
            variants={fadeInUp}
            transition={{ duration: 0.5, ease: "easeOut" }}
            data-testid="contact-button"
          >
            <Mail size={18} strokeWidth={1.5} />
            <span>BOOKING / CONTACT</span>
          </motion.button>
        </motion.div>
      </div>

      {/* Contact Modal */}
      <AnimatePresence>
        {showContactModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowContactModal(false)}
          >
            <motion.div
              className="modal-content"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                className="modal-close" 
                onClick={() => setShowContactModal(false)}
                data-testid="modal-close"
              >
                <X size={24} />
              </button>

              <h2 className="modal-title">CONTACT US</h2>
              <p className="modal-subtitle">For bookings and inquiries</p>

              {submitStatus === "success" ? (
                <div className="success-message">
                  <p>Message sent successfully!</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Your name"
                      data-testid="contact-name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your@email.com"
                      data-testid="contact-email"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Your message..."
                      rows={4}
                      data-testid="contact-message"
                    />
                  </div>

                  {submitStatus === "error" && (
                    <p className="error-message">Failed to send. Please try again.</p>
                  )}

                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={isSubmitting}
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      <Loader2 size={18} className="spinner" />
                    ) : (
                      <>
                        <Send size={18} />
                        <span>SEND MESSAGE</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

export default App;
